import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useTeams = (user, token, assignableUsers = [], showMessage) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMessages, setTeamMessages] = useState([]);
  const [newTeamMessage, setNewTeamMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  // Forms
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    manager_id: '',
  });

  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamForm, setEditTeamForm] = useState({
    name: '',
    description: '',
    manager_id: '',
  });

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [teamToAddMember, setTeamToAddMember] = useState(null);
  const [memberToAdd, setMemberToAdd] = useState('');

  // Fetch all teams
  const fetchTeams = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/teams/');
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Fetch teams error:', error);
    }
  }, [token]);

  // Fetch team messages
  const fetchTeamMessages = useCallback(async (teamId) => {
    if (!token || !teamId) return;
    try {
      const response = await apiClient(`/api/teams/${teamId}/messages`);
      if (response.ok) {
        const data = await response.json();
        setTeamMessages(data);
      }
    } catch (error) {
      console.error('Fetch team messages error:', error);
    }
  }, [token]);

  // Select team and fetch its conversation
  const handleSelectTeam = async (team) => {
    setSelectedTeam(team);
    if (team) {
      await fetchTeamMessages(team.id);
    } else {
      setTeamMessages([]);
    }
  };

  // Upload file attachment
  const handleFileUpload = async (file) => {
    if (!file) return null;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient('/api/teams/upload', {
        method: 'POST',
        isFormData: true,
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setUploadedMediaUrl(data.media_url);
        return data.media_url;
      } else {
        if (showMessage) showMessage('File upload failed', 'error');
        return null;
      }
    } catch (error) {
      if (showMessage) showMessage('Error uploading file', 'error');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Send team message
  const handleSendTeamMessage = async (e) => {
    if (e) e.preventDefault();
    if (!selectedTeam || (!newTeamMessage.trim() && !selectedFile)) return;

    setIsLoadingTeams(true);
    try {
      let mediaUrl = uploadedMediaUrl;
      if (selectedFile && !mediaUrl) {
        mediaUrl = await handleFileUpload(selectedFile);
      }

      const response = await apiClient(`/api/teams/${selectedTeam.id}/messages`, {
        method: 'POST',
        body: {
          content: newTeamMessage.trim(),
          media_url: mediaUrl || undefined,
        },
      });

      if (response.ok) {
        await fetchTeamMessages(selectedTeam.id);
        setNewTeamMessage('');
        setSelectedFile(null);
        setUploadedMediaUrl('');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to send team message', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error sending message', 'error');
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Create team
  const handleCreateTeam = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingTeams(true);

    try {
      const response = await apiClient('/api/teams/', {
        method: 'POST',
        body: {
          name: teamForm.name,
          description: teamForm.description,
          manager_id: teamForm.manager_id || undefined,
        },
      });

      if (response.ok) {
        await fetchTeams();
        setTeamForm({ name: '', description: '', manager_id: '' });
        if (showMessage) showMessage('Team created successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to create team', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error creating team', 'error');
      return false;
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Edit team
  const handleEditTeam = async (e) => {
    if (e) e.preventDefault();
    if (!editingTeam) return;

    setIsLoadingTeams(true);
    try {
      const response = await apiClient(`/api/teams/${editingTeam.id}`, {
        method: 'PUT',
        body: {
          name: editTeamForm.name,
          description: editTeamForm.description,
          manager_id: editTeamForm.manager_id || undefined,
        },
      });

      if (response.ok) {
        await fetchTeams();
        setEditingTeam(null);
        if (showMessage) showMessage('Team updated successfully!', 'success');
        return true;
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to update team', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating team', 'error');
      return false;
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Delete team
  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    setIsLoadingTeams(true);
    try {
      const response = await apiClient(`/api/teams/${teamId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchTeams();
        if (selectedTeam?.id === teamId) setSelectedTeam(null);
        if (showMessage) showMessage('Team deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to delete team', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error deleting team', 'error');
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Add team member
  const handleAddTeamMember = async () => {
    if (!teamToAddMember || !memberToAdd) return;

    setIsLoadingTeams(true);
    try {
      const response = await apiClient(`/api/teams/${teamToAddMember.id}/members`, {
        method: 'POST',
        body: { user_id: memberToAdd },
      });

      if (response.ok) {
        await fetchTeams();
        setShowAddMemberModal(false);
        setMemberToAdd('');
        setTeamToAddMember(null);
        if (showMessage) showMessage('Member added to team!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to add member', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error adding member', 'error');
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Remove team member
  const handleRemoveTeamMember = async (teamId, userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;

    setIsLoadingTeams(true);
    try {
      const response = await apiClient(`/api/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchTeams();
        if (showMessage) showMessage('Member removed from team!', 'success');
      } else {
        const errorData = await response.json();
        if (showMessage) showMessage(errorData.detail || 'Failed to remove member', 'error');
      }
    } catch (error) {
      if (showMessage) showMessage('Network error removing member', 'error');
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Helper methods
  const isTeamMember = (team, userId = user?.id) => {
    if (!team || !userId) return false;
    return team.members?.includes(userId) || team.manager_id === userId;
  };

  const canManageTeam = (team, userId = user?.id) => {
    if (!team || !userId) return false;
    return user?.role === 'admin' || user?.role === 'superadmin' || team.manager_id === userId;
  };

  const getTeamMembers = (team) => {
    if (!team || !team.members) return [];
    return assignableUsers.filter(u => team.members.includes(u.id));
  };

  return {
    teams,
    selectedTeam,
    setSelectedTeam,
    teamMessages,
    newTeamMessage,
    setNewTeamMessage,
    selectedFile,
    setSelectedFile,
    isUploading,
    isLoadingTeams,
    teamForm,
    setTeamForm,
    editingTeam,
    setEditingTeam,
    editTeamForm,
    setEditTeamForm,
    showAddMemberModal,
    setShowAddMemberModal,
    teamToAddMember,
    setTeamToAddMember,
    memberToAdd,
    setMemberToAdd,
    fetchTeams,
    fetchTeamMessages,
    handleSelectTeam,
    handleFileUpload,
    handleSendTeamMessage,
    handleCreateTeam,
    handleEditTeam,
    handleDeleteTeam,
    handleAddTeamMember,
    handleRemoveTeamMember,
    isTeamMember,
    canManageTeam,
    getTeamMembers,
  };
};
